import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { Skeleton } from '@art-of-hospitality-solutions-llc/ui-kit/components/skeleton';
import { DateTime } from 'luxon';
import { useShiftReport } from '@Hooks/useShiftReports';
import GuestProfileCard from './GuestProfileCard';

interface Props {
  reportId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ShiftReportSheet({
  reportId,
  open,
  onOpenChange,
}: Props) {
  const { data: report, status } = useShiftReport({
    id: reportId ?? undefined,
    enabled: open && !!reportId,
  });
  const shiftDate = report ? DateTime.fromISO(report.shiftDate) : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{report?.venueName ?? 'Shift Report'}</SheetTitle>
          {report && (
            <p className="text-xs text-zinc-500">
              {report.serviceLabel}
              {shiftDate?.isValid && <> · {shiftDate.toFormat('cccc, LLL d')}</>}
            </p>
          )}
        </SheetHeader>
        <SheetBody className="space-y-6">
          {status === 'pending' ? (
            <div className="space-y-3">
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : !report ? (
            <p className="text-sm text-zinc-500">Report not found.</p>
          ) : (
            <>
              {report.todaysVibe && (
                <div className="space-y-3">
                  <div className="text-center text-xs font-bold tracking-wide text-zinc-500 uppercase">
                    Today's Vibe
                  </div>
                  <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                    <div className="text-xs font-semibold text-zinc-900">
                      {report.todaysVibe.label}{' '}
                      <span className="font-normal text-zinc-500">
                        · {report.todaysVibe.coverCount} covers ·{' '}
                        {report.todaysVibe.guestCount} reservations ·{' '}
                        {report.todaysVibe.enrichedCount} enriched
                      </span>
                    </div>
                    <p className="mt-1 text-sm leading-relaxed text-zinc-700">
                      {report.todaysVibe.summary}
                    </p>
                  </div>
                </div>
              )}
              {report.timeBlocks.map((block) => (
                <div key={block.label} className="space-y-3">
                  <div className="text-center text-xs font-bold tracking-wide text-zinc-500 uppercase">
                    {block.label}
                  </div>
                  {block.guests.map((guest) => (
                    <GuestProfileCard key={guest.id} guest={guest} />
                  ))}
                </div>
              ))}
            </>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
